export const R_AUTH_BASE_URL = import.meta.env.VITE_R_AUTH_BASE_URL ?? "https://auth.rmcd.cc";
export const HANDOFF_AUDIENCE = "command-control";
export const TOKEN_STORAGE_KEY = "command-control.auth-token";

export type AuthSession = {
  user: {
    id: string;
    email: string;
    emailVerified: boolean;
    name: string;
    image?: string | null;
    role?: string;
  };
  session: {
    id: string;
    userId: string;
    token: string;
    expiresAt: string;
  };
};

export type HandoffResponse =
  | {
      ok: true;
      tokenType: "Bearer";
      token: string;
      expiresAt: string;
      user?: {
        id: string;
        email: string;
        emailVerified: boolean;
        name: string;
        image?: string | null;
        role?: string;
      };
    }
  | {
      ok: false;
      error: {
        code: "UNAUTHORIZED" | "FORBIDDEN" | "BAD_REQUEST" | string;
        message: string;
      };
    };

export type AuthHandoffSuccess = Extract<HandoffResponse, { ok: true }>;

function authBaseUrl(): string {
  return R_AUTH_BASE_URL.replace(/\/$/, "");
}

function handoffUser(input: {
  email: string;
  emailVerified?: boolean;
  id: string;
  image?: string | null;
  name?: string | null;
  role?: string | null;
}): NonNullable<AuthHandoffSuccess["user"]> {
  return {
    email: input.email,
    emailVerified: input.emailVerified ?? false,
    id: input.id,
    ...(input.image !== undefined ? { image: input.image } : {}),
    name: input.name || input.email,
    ...(input.role ? { role: input.role } : {}),
  };
}

export async function getAuthSession(token = storedAuthToken()): Promise<AuthSession | null> {
  const response = await fetch(`${authBaseUrl()}/rest/v1/auth/session`, {
    credentials: "include",
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
    },
  });

  if (response.status === 401) {
    return null;
  }

  if (!response.ok) {
    throw new Error(`r-auth session check failed: ${response.status}`);
  }

  return response.json() as Promise<AuthSession>;
}

export function authHandoffUrl(redirectTo: string): string {
  const url = new URL("/api/auth/handoff", authBaseUrl());
  url.searchParams.set("audience", HANDOFF_AUDIENCE);
  url.searchParams.set("redirectTo", redirectTo);
  return url.toString();
}

function parseHandoffParam(value: string | null): AuthHandoffSuccess | null {
  if (!value) {
    return null;
  }

  try {
    const parsed = JSON.parse(value) as Partial<AuthHandoffSuccess>;
    if (parsed.ok === true && parsed.token && parsed.expiresAt) {
      return {
        ok: true,
        tokenType: "Bearer",
        token: parsed.token,
        expiresAt: parsed.expiresAt,
        ...(parsed.user?.id && parsed.user.email
          ? {
              user: handoffUser(parsed.user),
            }
          : {}),
      };
    }
  } catch {
    return null;
  }

  return null;
}

function parseHandoffFields(search: URLSearchParams, hash: URLSearchParams): AuthHandoffSuccess | null {
  const token = search.get("token") ?? hash.get("token") ?? hash.get("accessToken");
  const expiresAt = search.get("expiresAt") ?? hash.get("expiresAt");
  const tokenType = search.get("tokenType") ?? hash.get("token_type") ?? hash.get("tokenType") ?? "Bearer";
  const id = search.get("userId") ?? hash.get("userId") ?? search.get("id") ?? hash.get("id");
  const email = search.get("email") ?? hash.get("email");

  if (tokenType !== "Bearer" || !token || !expiresAt) {
    return null;
  }

  return {
    ok: true,
    tokenType: "Bearer",
    token,
    expiresAt,
    ...(id && email
      ? {
          user: handoffUser({
            id,
            email,
            emailVerified: (search.get("emailVerified") ?? hash.get("emailVerified")) === "true",
            image: search.get("image") ?? hash.get("image"),
            name: search.get("name") ?? hash.get("name") ?? email,
            role: search.get("role") ?? hash.get("role"),
          }),
        }
      : {}),
  };
}

export function parseAuthHandoffUrl(href: string): AuthHandoffSuccess | null {
  const url = new URL(href);
  const hash = new URLSearchParams(url.hash.startsWith("#") ? url.hash.slice(1) : url.hash);
  const handoff = parseHandoffParam(url.searchParams.get("handoff") ?? hash.get("handoff"));

  return handoff ?? parseHandoffFields(url.searchParams, hash);
}

export async function signOutOfAuth(): Promise<void> {
  await fetch(`${authBaseUrl()}/api/auth/sign-out`, {
    method: "POST",
    credentials: "include",
  }).catch(() => undefined);
}

export function storedAuthToken(): string | null {
  return window.localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function storeAuthToken(token: string, expiresAt?: string): void {
  window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  if (expiresAt) {
    window.localStorage.setItem(`${TOKEN_STORAGE_KEY}.expiresAt`, expiresAt);
  }
}

export function clearStoredAuthToken(): void {
  window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  window.localStorage.removeItem(`${TOKEN_STORAGE_KEY}.expiresAt`);
}
